import { useCallback, useState } from 'react'
import { marked } from 'marked'

import EditIcon from '@mui/icons-material/Create'
import DeleteIcon from '@mui/icons-material/Delete'
import LoadingButton from '@mui/lab/LoadingButton'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Paper from '@mui/material/Paper'
import Grid from '@mui/material/Grid'
import { styled } from '@mui/material/styles'

import { ProductNote } from '~/common/schemas/product'
import { StoreNote } from '~/common/schemas/store'
import { useAppSelector } from '~/store'
import { api } from '~/api'

import ImageList from './image-list'
import ImageManager from './image-manager'
import MarkdownEditor from './markdown-editor'

type ObjectType = 'product' | 'store'
type NoteType<T extends ObjectType> = T extends 'product' ? ProductNote : StoreNote

type NoteProps<T extends ObjectType> = {
    mode: 'create' | 'update' | 'view' | 'placeholder'
    objectType: T
    objectId: number
    storeId: number
    note?: NoteType<T>
}

function Note<T extends ObjectType>({ mode, objectType, objectId, storeId, note }: NoteProps<T>) {
    const staticFilesDomain = useAppSelector((state) => state.initial.staticFilesDomain)

    // Create initial note based on object type
    const createInitialNote = (): Partial<NoteType<T>> => {
        if (objectType === 'product') {
            return { product: objectId } as Partial<NoteType<T>>
        } else {
            return { store: objectId } as Partial<NoteType<T>>
        }
    }

    const [currentNote, setCurrentNote] = useState<Partial<NoteType<T>>>(
        note || createInitialNote()
    )
    const [currentMode, setCurrentMode] = useState(mode)
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

    const images = (currentNote.media || []).map((item) => ({ ...item, image: item.media }))
    const disableSaveButton = !currentNote.content || !currentNote.title

    // Generic API calls based on object type
    const getApiEndpoints = () => {
        if (objectType === 'product') {
            return {
                createNote: api.products.createNote,
                updateNote: api.products.updateNote,
                deleteNote: api.products.deleteNote,
                invalidateKey: ['products', { productId: objectId }, 'notes'] as const,
                params: { productId: objectId },
            }
        } else {
            return {
                createNote: api.stores.createNote,
                updateNote: api.stores.updateNote,
                deleteNote: api.stores.deleteNote,
                invalidateKey: ['stores', { storeId: objectId }, 'notes'] as const,
                params: { storeId: objectId },
            }
        }
    }

    const endpoints = getApiEndpoints()

    const { mutate: createNote, isLoading: isCreating } = endpoints.createNote.useMutation({
        meta: {
            invalidates: [endpoints.invalidateKey],
        },
        onSuccess: () => {
            setCurrentMode('placeholder')
            setCurrentNote((prev) => ({ ...prev, title: '', content: '' }))
        },
    })

    const { mutate: updateNote, isLoading: isUpdating } = endpoints.updateNote.useMutation({
        meta: {
            invalidates: [endpoints.invalidateKey],
        },
        onSuccess: () => {
            setCurrentMode('view')
        },
    })

    const { mutate: deleteNote, isLoading: isDeleting } = endpoints.deleteNote.useMutation({
        meta: {
            invalidates: [endpoints.invalidateKey],
        },
        onSuccess: () => {
            setShowDeleteConfirm(false)
        },
    })

    const handleImageChange = useCallback((files: NoteType<T>['media'], callback?: () => void) => {
        setCurrentNote((prev) => ({
            ...prev,
            media: files.map((item) => ({ ...item, media: item.image, description: item.name })),
        }))
        callback?.()
    }, [])

    const handleSave = () => {
        if (currentMode === 'create') {
            createNote({
                body: { ...currentNote },
                params: endpoints.params,
            })
        } else if (currentNote.id) {
            const updateParams =
                objectType === 'product'
                    ? { productId: objectId, noteId: currentNote.id }
                    : { storeId: objectId, noteId: currentNote.id }

            updateNote({
                body: { ...currentNote },
                params: updateParams,
            })
        }
    }

    const handleDelete = () => {
        if (currentNote?.id) {
            const deleteParams =
                objectType === 'product'
                    ? { productId: objectId, noteId: currentNote.id }
                    : { storeId: objectId, noteId: currentNote.id }

            deleteNote({ params: deleteParams })
        }
    }

    return (
        <Box
            sx={{
                position: 'relative',
                '&:hover': {
                    '> div[aria-role=action-buttons]': {
                        display: 'block',
                    },
                },
                p: 1,
            }}
        >
            {currentMode === 'view' && (
                <Box
                    aria-role="action-buttons"
                    sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        display: 'none',
                    }}
                >
                    <IconButton
                        aria-label="update"
                        size="small"
                        onClick={() => setCurrentMode('update')}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        aria-label="delete"
                        size="small"
                        onClick={() => setShowDeleteConfirm(true)}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            )}
            {currentMode === 'view' ? (
                <>
                    {currentNote.title && (
                        <Typography
                            variant="caption"
                            color="textSecondary"
                            style={{ fontSize: '1em', fontWeight: 'bold' }}
                        >
                            {currentNote.title || 'Title'}
                        </Typography>
                    )}
                    {currentNote.content && (
                        <div
                            style={{ overflow: 'auto' }}
                            dangerouslySetInnerHTML={{ __html: marked(currentNote.content) }}
                        />
                    )}
                </>
            ) : (
                <>
                    <TextField
                        fullWidth
                        placeholder={gettext('Title')}
                        value={currentMode === 'placeholder' ? '' : currentNote.title}
                        variant="outlined"
                        margin="dense"
                        size="small"
                        required
                        onFocus={() => {
                            if (currentMode === 'placeholder') {
                                setCurrentMode('create')
                            }
                        }}
                        onChange={(e) =>
                            setCurrentNote((prev) => ({ ...prev, title: e.target.value }))
                        }
                    />
                    <MarkdownEditor
                        style={{
                            height: 150,
                            marginBottom: 16,
                        }}
                        value={currentMode === 'placeholder' ? '' : currentNote.content || ''}
                        onChange={(val) => {
                            if (currentMode === 'placeholder') {
                                setCurrentMode('create')
                            }
                            setCurrentNote((prev) => ({ ...prev, content: val }))
                        }}
                    />
                </>
            )}
            {!!currentNote?.media?.length && currentMode === 'view' && (
                <ImageList images={currentNote.media} staticFilesDomain={staticFilesDomain} />
            )}
            {(currentMode === 'create' || currentMode === 'update') && (
                <Stack style={{ marginTop: 10 }} gap={1}>
                    <ImageManager
                        storeID={storeId}
                        images={images}
                        imagesChangedHandler={handleImageChange}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            disabled={isCreating || isUpdating}
                            size="small"
                            onClick={() => {
                                setCurrentMode(currentMode === 'create' ? 'placeholder' : 'view')
                                setCurrentNote({ ...note })
                            }}
                            color="inherit"
                        >
                            {gettext('Cancel')}
                        </Button>
                        <LoadingButton
                            disableElevation
                            loading={isCreating || isUpdating}
                            size="small"
                            variant="contained"
                            color="secondary"
                            sx={{ mr: 0.5 }}
                            disabled={disableSaveButton}
                            onClick={handleSave}
                        >
                            {gettext('Save')}
                        </LoadingButton>
                    </Box>
                </Stack>
            )}
            <Dialog
                open={showDeleteConfirm}
                aria-labelledby="dialog-title"
                onClose={() => setShowDeleteConfirm(false)}
            >
                <DialogTitle id="dialog-title">{gettext('Confirmation')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {gettext('Delete this note?')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button size="small" autoFocus onClick={() => setShowDeleteConfirm(false)}>
                        {gettext('Cancel')}
                    </Button>
                    <LoadingButton
                        loading={isDeleting}
                        size="small"
                        variant="contained"
                        color="secondary"
                        onClick={handleDelete}
                    >
                        {gettext('Delete')}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

type Props<T extends ObjectType> = {
    objectType: T
    objectId: number
    storeId: number
}

const Item = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
    ...theme.typography.body2,
    padding: theme.spacing(1),
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.grey[300]}}`,
}))

export default function Notes<T extends ObjectType>({ objectType, objectId, storeId }: Props<T>) {
    // Generic query based on object type
    const getQueryConfig = () => {
        if (objectType === 'product') {
            return {
                queryKey: ['products', { productId: objectId }, 'notes'] as const,
                queryFn: api.products.notes,
                params: { productId: objectId },
            }
        } else {
            return {
                queryKey: ['stores', { storeId: objectId }, 'notes'] as const,
                queryFn: api.stores.notes,
                params: { storeId: objectId },
            }
        }
    }

    const queryConfig = getQueryConfig()
    const { data } = queryConfig.queryFn.useQuery(queryConfig.queryKey, {
        params: queryConfig.params,
    })

    return (
        <Paper style={{ position: 'relative', padding: '15px 20px' }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    mx: -2.5,
                    px: 2.5,
                    pb: 1,
                    marginBottom: '20px',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%',
                        height: '100%',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="subtitle1">{gettext('Notes')}</Typography>
                    </div>
                </Box>
            </Box>
            <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={2}>
                    {!!data?.body.length &&
                        data.body.map((note) => (
                            <Grid item xs={4} key={note.id}>
                                <Item>
                                    <Note
                                        mode="view"
                                        objectType={objectType}
                                        note={note as NoteType<T>}
                                        objectId={objectId}
                                        storeId={storeId}
                                    />
                                </Item>
                            </Grid>
                        ))}
                    <Grid item xs={4}>
                        <Item>
                            <Note
                                mode="create"
                                objectType={objectType}
                                objectId={objectId}
                                storeId={storeId}
                            />
                        </Item>
                    </Grid>
                </Grid>
            </Box>
        </Paper>
    )
}
